import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { OnboardingService } from '../Services/onboarding.service';
import { OnboardingSessionService } from '../Services/onboarding-session.service';
import { OnboardingConfigService } from '../Services/onboarding-config.service';
import { PlanService } from '../Services/plan.service';
import { CompanyData, OnboarderData, Onboarding } from '../Models/onboarding.model';
import { OnboardingConfigInterface } from '../Interfaces/config.interface';
import { LaravelDefaultResponse } from '../Interfaces/response.interface';
import { Plan } from '../Interfaces/plan.interface';
import { CheckoutPreview } from '../Interfaces/checkout-preview.interface';

@Injectable({
  providedIn: 'root',
})
export class OnboardingFacade {
  private readonly _onboarding = inject(OnboardingService);
  private readonly _session = inject(OnboardingSessionService);
  private readonly _config = inject(OnboardingConfigService);
  private readonly _plan = inject(PlanService);

  private readonly _onboardingData = signal<Onboarding | null>(null);
  private readonly _onboardingLoaded = signal<boolean>(false);

  readonly graphPermissions = this._config.graphPermissions;
  readonly callbackUrl = this._config.callbackUrl;
  readonly configLoaded = this._config.configLoaded;
  readonly onboarding = this._onboardingData.asReadonly();
  readonly onboardingLoaded = this._onboardingLoaded.asReadonly();

  get(options: { skipLoading?: boolean } = {}): Observable<Onboarding | null> {
    return this._onboarding.get(options).pipe(
      tap((onboarding) => {
        this._onboardingData.set(onboarding);
        this._onboardingLoaded.set(true);
      })
    );
  }

  saveCompanyAndOnboarderData(data: {
    company_data: CompanyData;
    onboarder_data: OnboarderData;
  }): Observable<Onboarding> {
    return this._onboarding.saveCompanyAndOnboarderData(data);
  }

  getAdminConsentUrl(): Observable<LaravelDefaultResponse<string>> {
    return this._onboarding.getAdminConsentUrl();
  }

  complete(
    preferences: { preferred_lang: string; preferred_theme: string },
    options: { skipLoading?: boolean } = {}
  ): Observable<void> {
    return this._onboarding.complete(preferences, options);
  }

  createSession(): Observable<Onboarding> {
    return this._session.createSession();
  }

  getOnboardingConfig(options: { skipLoading?: boolean } = {}): Observable<OnboardingConfigInterface> {
    return this._config.getOnboardingConfig(options);
  }

  getPlans(options: { skipLoading?: boolean } = {}): Observable<Plan[]> {
    return this._plan.getPlans(options);
  }

  savePlan(planId: string, offerType: string): Observable<Onboarding> {
    return this._plan.savePlan(planId, offerType);
  }

  saveLicences(userLicences: number, deviceLicences: number): Observable<Onboarding> {
    return this._plan.saveLicences(userLicences, deviceLicences);
  }

  getCheckoutPreview(options: { skipLoading?: boolean } = {}): Observable<CheckoutPreview> {
    return this._plan.getCheckoutPreview(options);
  }

  initiateCheckout(): Observable<{ checkout_url: string }> {
    return this._plan.initiateCheckout();
  }

  verifyCheckout(
    sessionId: string,
    options: { skipLoading?: boolean } = {}
  ): Observable<LaravelDefaultResponse<unknown>> {
    return this._plan.verifyCheckout(sessionId, options);
  }
}
